import { CommonApplication, CommonApplicationGroup } from '@codidae/common-types';
import { useQuery } from '@tanstack/react-query';
import { AppGroupPage } from 'components/app-group-page/app-group-page';
import Card from 'components/card/card';
import Flex from 'components/flex/flex';
import ScreenUsageChart from 'components/screen-usage-chart/screen-usage-chart';
import StatsTimeRangeSelector from 'components/stats-time-range-selector/stats-time-range-selector';
import { useUserContext } from 'contexts/user-context';
import { useMemo, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { getScreentimeForPeriod, getScreentimeLimits } from 'services/screentime';
import { defaultTimePeriod, formatTime, getDaysFromTimePeriod } from 'utils/date';
import styles from './parent-stats-screentime.module.scss';

/* eslint-disable-next-line */
export interface ParentStatsScreentimeProps {
  childrenId: string;
}

const colors = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#FF6384',
];

export function ParentStatsScreentime({ childrenId }: ParentStatsScreentimeProps) {
  const { getSubscriptionStatus } = useUserContext();
  const [shoudLock, setShouldLock] = useState(false);
  const [timeRangeName, setTimeRangeName] = useState('');
  const [timePeriod, setTimePeriod] = useState<{
    start: Date;
    end: Date;
  }>(defaultTimePeriod);
  const { data: screentimes } = useQuery({
    queryKey: ['getScreentimeForPeriod', childrenId, timePeriod],
    queryFn: () => getScreentimeForPeriod(Number(childrenId), timePeriod.start, timePeriod.end),
  });
  const { data: screentimeLimits } = useQuery({
    queryKey: ['getScreentimeLimits', childrenId],
    queryFn: () => getScreentimeLimits(Number(childrenId)),
  });
  const [displayBy, setDisplayBy] = useState<'apps' | 'groups'>('apps');
  const [appOpened, setAppOpened] = useState<number | null>(null);
  const [groupOpened, setGroupOpened] = useState<number | null>(null);

  const locked = shoudLock && getSubscriptionStatus(Number(childrenId)) === 'freemium';

  const dailyUsages = useMemo(() => {
    if (!screentimes) return [];
    const dus = [] as {
      createdAt: Date;
      usage: number;
    }[];
    screentimes.forEach((screentime) => {
      screentime.screentimes.forEach((st) => {
        const date = new Date(st.createdAt);
        const existing = dus.find((daily) => daily.createdAt.toDateString() === date.toDateString());
        if (existing) {
          existing.usage += Math.round(st.duration / 60);
        } else {
          dus.push({
            createdAt: date,
            usage: Math.round(st.duration / 60),
          });
        }
      });
    });
    dus.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    return dus;
  }, [screentimes]);

  const { periodUsage, lastPeriodUsage } = useMemo(() => {
    const periodDays = getDaysFromTimePeriod(timePeriod);
    const lastPeriodDays = getDaysFromTimePeriod({
      start: new Date(timePeriod.start.getTime() - periodDays.length * 24 * 60 * 60 * 1000),
      end: new Date(timePeriod.end.getTime() - periodDays.length * 24 * 60 * 60 * 1000),
    });
    const computedPeriodUsage = dailyUsages
      ?.filter((use) => periodDays.some((date) => new Date(use.createdAt).toDateString() === date.toDateString()))
      .reduce((acc, use) => acc + use.usage, 0);
    const computedLastPeriodUsage = dailyUsages
      ?.filter((use) => lastPeriodDays.some((date) => new Date(use.createdAt).toDateString() === date.toDateString()))
      .reduce((acc, use) => acc + use.usage, 0);
    return { periodUsage: computedPeriodUsage, lastPeriodUsage: computedLastPeriodUsage };
  }, [dailyUsages, timePeriod]);

  const periodPercentage = useMemo(() => {
    if (lastPeriodUsage === 0) return 0;
    return Math.round(((periodUsage - lastPeriodUsage) / lastPeriodUsage) * 100);
  }, [lastPeriodUsage, periodUsage]);

  const mergedScreentime = useMemo(() => {
    if (!screentimes) return [];

    const merged = [] as {
      application: { id: number; name: string; group: CommonApplicationGroup; icon: string };
      usage: number;
    }[];

    screentimes.forEach((screentime) => {
      screentime.screentimes.forEach((st) => {
        const appId = st.application.id;
        const existing = merged.find((s) => s.application.id === appId);

        if (existing) {
          existing.usage += st.duration;
        } else {
          merged.push({
            application: {
              id: appId,
              name: st.application.name,
              group: st.application.groups[0],
              icon: st.application.icon,
            },
            usage: st.duration,
          });
        }
      });
    });
    merged.sort((a, b) => b.usage - a.usage);
    return merged;
  }, [screentimes]);

  const groupedByGroup = useMemo(() => {
    const grouped = {} as { [key: string]: { id: number; name: string; usage: number; icon: string }[] };
    const groups = mergedScreentime.map((screentime) => screentime.application.group).filter(Boolean);
    mergedScreentime.forEach((screentime) => {
      const groupId = screentime.application.group?.id;
      if (!groupId) return;
      if (!grouped[groupId]) {
        grouped[groupId] = [];
      }
      grouped[groupId].push({
        id: screentime.application.id,
        name: screentime.application.name,
        usage: screentime.usage,
        icon: screentime.application.icon,
      });
    });
    return Object.entries(grouped).map(([groupId, apps], index) => ({
      id: Number(groupId),
      name: groups.find((group) => group.id === Number(groupId))?.name,
      apps,
      totalUsage: apps.reduce((acc, app) => acc + app.usage, 0),
      color: colors[index % colors.length],
    }));
  }, [mergedScreentime]);

  return (
    <>
      <StatsTimeRangeSelector
        onTimePeriodChange={setTimePeriod}
        onShouldLock={setShouldLock}
        onTimeRangeNameChange={setTimeRangeName}
        style={{ border: 'none', paddingTop: 0 }}
      />
      <Flex gap={20} direction="column" width="100%">
        <Card style={{ padding: 15, width: '100%' }}>
          <div className={styles.title}>Temps d&apos;écran</div>

          <div className={styles.periodUsage}>{formatTime(periodUsage)}</div>
          {periodPercentage !== 0 && (
            <div className={styles.comparaison}>
              {periodPercentage > 0 ? (
                <span className={styles.positive}>+{periodPercentage}%</span>
              ) : (
                <span className={styles.negative}>-{periodPercentage}%</span>
              )}{' '}
              par rapport à la période précédente
            </div>
          )}
          <br />
          {!!dailyUsages?.length && (
            <ScreenUsageChart
              dailyUsages={dailyUsages}
              locked={locked}
              timeRangeName={timeRangeName}
              timePeriod={timePeriod}
              noTitle
            />
          )}
        </Card>
        <Card style={{ padding: 15, width: '100%' }}>
          <Flex gap={10} width="100%" style={{ marginBottom: 10 }}>
            <button
              type="button"
              className={styles.button + (displayBy === 'apps' ? ` ${styles.active}` : '')}
              onClick={() => setDisplayBy('apps')}
            >
              Par apps
            </button>
            <button
              type="button"
              className={styles.button + (displayBy === 'groups' ? ` ${styles.active}` : '')}
              onClick={() => setDisplayBy('groups')}
            >
              Par groupe
            </button>
          </Flex>
          {displayBy === 'apps' ? (
            <>
              <div className={styles.title}>Classement des apps utilisées</div>

              <Flex gap={5} direction="column" width="100%" style={{ marginTop: 20 }}>
                {mergedScreentime?.map((screentime) => {
                  const limit = screentimeLimits?.find(
                    (l) => l.application && l.application.id === screentime.application.id,
                  );
                  return (
                    <div
                      key={screentime.application.id}
                      className={styles.app}
                      onClick={() => setAppOpened(screentime.application.id)}
                    >
                      {screentime.application.icon ? (
                        <img
                          className={styles.appIcon}
                          src={`data:image/png;base64,${screentime.application.icon}`}
                          alt="app icon"
                        />
                      ) : (
                        <div className={styles.appIcon} />
                      )}
                      <div className={styles.appName}>
                        <div className={styles.name}>{screentime.application.name}</div>
                        {limit && (
                          <div className={styles.progressBar}>
                            <div
                              className={
                                `${styles.progress} ${limit.duration / 2 < screentime.usage ? styles.orange : ''}` +
                                ` ${limit.duration * 0.75 < screentime.usage ? styles.red : ''}`
                              }
                              style={{
                                width: `${Math.min(Math.round((screentime.usage / limit.duration) * 100), 100)}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className={styles.appTime}>
                        {formatTime(Math.round(screentime.usage / 60))}{' '}
                        {limit && <span className={styles.limit}>/ {formatTime(Math.round(limit.duration / 60))}</span>}
                      </div>
                    </div>
                  );
                })}
              </Flex>
            </>
          ) : (
            <>
              <div className={styles.title}>Par groupe</div>
              <div style={{ margin: 40 }}>
                <Pie
                  data={{
                    labels: groupedByGroup.map((group) => group.name),
                    datasets: [
                      {
                        label: 'Screentime',
                        data: groupedByGroup.map((group) => group.totalUsage),
                        backgroundColor: groupedByGroup.map((group) => group.color),
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          title: (tooltipItem) => ``,
                          label: (tooltipItem) => {
                            const val = Math.round((tooltipItem.raw as number) / 60);
                            if (val < 60) {
                              return `${tooltipItem.label}: ${val}m`;
                            }
                            return `${tooltipItem.label}: ${Math.floor(val / 60)}h${val % 60 === 0 ? '' : ` ${val % 60}m`}`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
              <Flex gap={5} direction="column" width="100%" style={{ marginTop: 20 }}>
                {groupedByGroup?.map((group) => {
                  const limit = screentimeLimits?.find((l) => l.group && l.group.id === group.id);
                  return (
                    <div key={group.id} className={styles.app} onClick={() => setGroupOpened(group.id)}>
                      <div className={styles.appIcon} style={{ backgroundColor: group.color }} />
                      <div className={styles.appName}>
                        <div className={styles.name}>{group.name}</div>
                        {limit && (
                          <div className={styles.progressBar}>
                            <div
                              className={
                                `${styles.progress} ${limit.duration / 2 < group.totalUsage ? styles.orange : ''}` +
                                ` ${limit.duration * 0.75 < group.totalUsage ? styles.red : ''}`
                              }
                              style={{
                                width: `${Math.min(Math.round((group.totalUsage / limit.duration) * 100), 100)}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className={styles.appTime}>
                        {formatTime(Math.round(group.totalUsage / 60))}
                        {limit && (
                          <span className={styles.limit}> / {formatTime(Math.round(limit.duration / 60))}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Flex>
            </>
          )}
        </Card>
        {appOpened && (
          <AppGroupPage
            application={
              mergedScreentime.find((screentime) => screentime.application.id === appOpened)?.application as
                | CommonApplication
                | undefined
            }
            type="app"
            key={appOpened}
            onClose={() => setAppOpened(null)}
            childrenId={childrenId}
            period={timePeriod}
            timeRangeName={timeRangeName}
          />
        )}
        {groupOpened && (
          <AppGroupPage
            group={groupedByGroup.find((group) => group.id === groupOpened) as CommonApplicationGroup | undefined}
            type="group"
            key={groupOpened}
            onClose={() => setGroupOpened(null)}
            childrenId={childrenId}
            period={timePeriod}
            timeRangeName={timeRangeName}
          />
        )}
      </Flex>
    </>
  );
}

export default ParentStatsScreentime;
